/**
 * Column definitions for the dashboard analytics tables. Parity:
 * `dashboard.html:1255-1294`. Two tables, each with a pinned first
 * column (`username` / `account_name`) that cannot be reordered.
 *
 * The column order is persisted per table to `localStorage` so the
 * user's hand-rolled arrangement survives refreshes. If the saved
 * column set doesn't match the current defaults (e.g. after a
 * column is added in a release) the saved order is discarded and
 * defaults are restored.
 */

export type DashboardColumnType = "string" | "number"

export interface DashboardColumn {
  id: string
  label: string
  type: DashboardColumnType
  tooltip?: string
}

export const DASHBOARD_BUYER_COLUMNS: readonly DashboardColumn[] = [
  { id: "username", label: "Buyer", type: "string" },
  { id: "role", label: "Role", type: "string" },
  {
    id: "balance",
    label: "Balance",
    type: "number",
    tooltip: "Sum of cached balances on active assigned ad accounts",
  },
  {
    id: "active_campaigns",
    label: "Act. campaign",
    type: "number",
    tooltip: "Distinct ENABLE campaigns with spend/revenue in the date range",
  },
  {
    id: "accounts_active",
    label: "Ad acc",
    type: "number",
    tooltip: "Accounts with activity in range / total assigned accounts",
  },
  { id: "cost", label: "Cost", type: "number" },
  { id: "revenue", label: "Revenue", type: "number" },
  { id: "profit", label: "Profit", type: "number" },
  { id: "clicks", label: "Click", type: "number" },
  { id: "ctr", label: "CTR", type: "number" },
  { id: "conversions", label: "Conv", type: "number" },
  { id: "gclicks", label: "gClick", type: "number" },
  { id: "cpm", label: "CPM", type: "number" },
  { id: "cpa", label: "CPA", type: "number", tooltip: "Cost per conversion (cost \u00f7 gclicks)" },
  { id: "rpc", label: "RPC", type: "number", tooltip: "Revenue per conversion (revenue \u00f7 gclicks)" },
  { id: "roi", label: "ROI", type: "number" },
  { id: "cvr", label: "CVR", type: "number" },
] as const

export const DASHBOARD_ACCOUNT_COLUMNS: readonly DashboardColumn[] = [
  { id: "account_name", label: "Account", type: "string" },
  { id: "tiktok_url", label: "TikTok", type: "string" },
  { id: "network", label: "Network", type: "string" },
  { id: "advertiser_status", label: "Status", type: "string" },
  { id: "balance", label: "Balance", type: "number" },
  { id: "cost", label: "Cost", type: "number" },
  { id: "revenue", label: "Revenue", type: "number" },
  { id: "profit", label: "Profit", type: "number" },
  { id: "impressions", label: "Impr", type: "number" },
  { id: "clicks", label: "Click", type: "number" },
  { id: "ctr", label: "CTR", type: "number" },
  { id: "conversions", label: "Conv", type: "number" },
  { id: "gclicks", label: "gClick", type: "number" },
  { id: "cpc", label: "CPC", type: "number" },
  { id: "cpm", label: "CPM", type: "number" },
  { id: "cpa", label: "CPA", type: "number", tooltip: "Cost per conversion (cost \u00f7 gclicks)" },
  { id: "rpc", label: "RPC", type: "number", tooltip: "Revenue per conversion (revenue \u00f7 gclicks)" },
  { id: "roi", label: "ROI", type: "number" },
  { id: "cvr", label: "CVR", type: "number" },
] as const

export const DASHBOARD_FIRST_COL_BUYER = "username"
export const DASHBOARD_FIRST_COL_ACCOUNT = "account_name"

export const DASHBOARD_BUYER_COLUMN_STORAGE_KEY = "dashboardBuyerColumnOrder"
export const DASHBOARD_ACCOUNT_COLUMN_STORAGE_KEY = "dashboardAccountColumnOrder"

/**
 * Normalise an order array so the pinned column is always first.
 * Parity: `_normalizeBuyerColumnOrder` / `_normalizeAccountColumnOrder`.
 */
export function normalizeOrder(
  order: readonly string[],
  defaults: readonly DashboardColumn[],
  pinnedId: string,
): string[] {
  if (!order.length) return defaults.map((c) => c.id)
  const rest = order.filter((id) => id !== pinnedId)
  return [pinnedId, ...rest]
}

/**
 * Load the saved column order. If the saved set's IDs don't match
 * the current defaults (new column added / removed upstream), we
 * discard the save and return the defaults. Parity:
 * `getDashboardBuyerColumnOrder` (dashboard.html:1308-1324).
 */
export function loadColumnOrder(
  storageKey: string,
  defaults: readonly DashboardColumn[],
  pinnedId: string,
): string[] {
  try {
    const saved = globalThis.localStorage?.getItem(storageKey)
    if (saved) {
      const parsed: unknown = JSON.parse(saved)
      if (Array.isArray(parsed)) {
        // The legacy format stored full column objects; we accept
        // both `{id, ...}` and bare string IDs so a user upgrading
        // from the Jinja page doesn't lose their layout.
        const ids = parsed.map((entry) =>
          typeof entry === "string"
            ? entry
            : entry && typeof entry === "object" && "id" in entry
              ? String((entry as { id: unknown }).id)
              : "",
        )
        const savedSet = new Set(ids.filter(Boolean))
        const defaultSet = new Set(defaults.map((c) => c.id))
        if (
          savedSet.size === defaultSet.size &&
          [...savedSet].every((id) => defaultSet.has(id))
        ) {
          return normalizeOrder(ids, defaults, pinnedId)
        }
      }
    }
  } catch {
    // Fall through to defaults.
  }
  return defaults.map((c) => c.id)
}

/**
 * Persist the column order under the given key. Stores bare string
 * IDs (slimmer than the legacy `{id,label,type}[]` payload but
 * accepted by `loadColumnOrder` above).
 */
export function saveColumnOrder(storageKey: string, order: string[]): void {
  try {
    globalThis.localStorage?.setItem(storageKey, JSON.stringify(order))
  } catch {
    // Storage may be disabled (private mode, quota exceeded) — the
    // in-memory state already drives the UI, so silently ignore.
  }
}
